export const APP_VERSION_STORAGE_KEY = 'app_version';
export const APP_VERSION_TIMESTAMP_STORAGE_KEY = 'app_version_timestamp';
export const LATEST_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY =
  'latest_frontend_version_timestamp';
export const RELOADED_FRONTEND_VERSION_TIMESTAMP_STORAGE_KEY =
  'reloaded_frontend_version_timestamp';
export const HEADER_FRONT_VERSION = 'HeaderFrontVersion';
export const APP_VERSION_TIMEZONE_OFFSET = '+03:00';

export const toUnixSeconds = (value?: string | null): number | null => {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;

    if (n >= 1e12) return Math.floor(n / 1000);

    return n;
  }

  const timezoneLessIsoDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(
    raw
  );
  const ms = Date.parse(
    timezoneLessIsoDateTime ? `${raw}${APP_VERSION_TIMEZONE_OFFSET}` : raw
  );
  if (Number.isNaN(ms)) return null;

  return Math.floor(ms / 1000);
};

export const normalizeAppVersionTimestamp = (value?: string | null): number =>
  toUnixSeconds(value) ?? 0;

export const getFrontendVersionHeaderValue = (
  appVersionTimestamp: number
): string => String(appVersionTimestamp);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const parseFrontendUpdatePayload = (payload: unknown): unknown => {
  if (typeof payload !== 'string') return payload;

  try {
    return JSON.parse(payload) as unknown;
  } catch {
    return null;
  }
};

export const getLatestFrontendVersionTimestamp = (
  frontendUpdateEvent: unknown
): number | null => {
  const payload = isRecord(frontendUpdateEvent)
    ? frontendUpdateEvent.jsonData ??
      frontendUpdateEvent.JsonData ??
      frontendUpdateEvent
    : frontendUpdateEvent;

  const parsedPayload = parseFrontendUpdatePayload(payload);
  if (!isRecord(parsedPayload)) {
    return toUnixSeconds(parsedPayload == null ? null : String(parsedPayload));
  }

  const latestVersionTimestamp =
    parsedPayload.VersionLatest ??
    parsedPayload.VersionLastest ??
    parsedPayload.versionLatest ??
    parsedPayload.versionLastest ??
    parsedPayload.LatestVersion ??
    parsedPayload.latestVersion;

  return toUnixSeconds(
    latestVersionTimestamp == null ? null : String(latestVersionTimestamp)
  );
};

export const isFrontendVersionOutdated = (
  latestVersionTimestamp: unknown,
  currentVersionTimestamp: number
): boolean => {
  if (!currentVersionTimestamp) return false;

  const latestVersionUnixSeconds = toUnixSeconds(
    latestVersionTimestamp == null ? null : String(latestVersionTimestamp)
  );

  if (!latestVersionUnixSeconds) return false;

  return latestVersionUnixSeconds > currentVersionTimestamp;
};

// Баннер «Доступно обновление» нужен, пока последняя версия новее текущей
// сборки. Если перезагрузка по баннеру ради этой версии вернула тот же бандл,
// новее сборки стенд не отдаёт — баннер снова не показывается, пока не придёт
// версия новее той, ради которой перезагружались (issue/11217).
export const isFrontendUpdateAvailable = (
  latestVersionTimestamp: number | null,
  currentVersionTimestamp: number,
  reloadedForVersionTimestamp: number | null
): boolean => {
  if (
    !latestVersionTimestamp ||
    !isFrontendVersionOutdated(latestVersionTimestamp, currentVersionTimestamp)
  ) {
    return false;
  }

  return (
    !reloadedForVersionTimestamp ||
    latestVersionTimestamp > reloadedForVersionTimestamp
  );
};
